/**
 * Professional Narrative View
 *
 * Renders the Professional Narrative — a prose document that captures
 * who a professional is, synthesized from their conversations.
 *
 * This is the primary public artifact for professional representation.
 */

import type { ProfileLinks, BetaEntity } from '../types/profile';
import { companyToSectionId } from '../utils/section-ids';

export interface NarrativeSection {
	type: 'identity' | 'arc' | 'role' | 'expertise' | 'perspective';
	heading: string;
	content: string;
	company?: string;
}

export interface ProfessionalNarrative {
	generatedAt: number;
	format: string;
	depth: 'baseline' | 'full';
	sections: NarrativeSection[];
	provenance: {
		claimCount: number;
		sessionCount: number;
	};
}

interface Props {
	narrative: ProfessionalNarrative;
	name?: string;
	title?: string;
	tagline?: string;
	focusAreas?: string[];
	isPublic?: boolean;
	profileLinks?: ProfileLinks;
	slug?: string;
	focusedCompany?: string | null;
	betaEntities?: BetaEntity[];
}

function formatDate(timestamp: number): string {
	return new Date(timestamp).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function hasLinks(links: ProfileLinks): boolean {
	return !!(links.resumeUrl || links.linkedinUrl || links.portfolioUrl || links.email);
}

export function NarrativeView({
	narrative,
	name,
	title,
	tagline,
	focusAreas,
	isPublic = false,
	profileLinks,
	slug,
	focusedCompany,
	betaEntities,
}: Props) {
	// Group sections by type
	const identitySection = narrative.sections.find(s => s.type === 'identity');
	const arcSection = narrative.sections.find(s => s.type === 'arc');
	const roleSections = narrative.sections.filter(s => s.type === 'role');
	const expertiseSection = narrative.sections.find(s => s.type === 'expertise');
	const perspectiveSection = narrative.sections.find(s => s.type === 'perspective');

	return (
		<div class={`narrative-container ${isPublic ? 'narrative-public' : ''}`}>
			<article class="narrative">
				{/* ── Identity Header ─────────────────────── */}
				<header class="narrative-header">
					<h1 class="narrative-name">{name || 'Professional Narrative'}</h1>
					{title && <p class="narrative-title">{title}</p>}
					{tagline && <p class="narrative-tagline">{tagline}</p>}
					{focusAreas && focusAreas.length > 0 && (
						<div class="narrative-focus">
							{focusAreas.map((area, i) => (
								<span key={i} class="focus-tag">{area}</span>
							))}
						</div>
					)}
					{profileLinks && hasLinks(profileLinks) && (
						<div class="narrative-links">
							{profileLinks.resumeUrl && (
								<a href={profileLinks.resumeUrl} target="_blank" rel="noopener noreferrer" class="narrative-link">Résumé</a>
							)}
							{profileLinks.linkedinUrl && (
								<a href={profileLinks.linkedinUrl} target="_blank" rel="noopener noreferrer" class="narrative-link">LinkedIn</a>
							)}
							{profileLinks.portfolioUrl && (
								<a href={profileLinks.portfolioUrl} target="_blank" rel="noopener noreferrer" class="narrative-link">Portfolio</a>
							)}
							{profileLinks.email && (
								<a href={`mailto:${profileLinks.email}`} class="narrative-link">Contact</a>
							)}
						</div>
					)}
				</header>

				{/* ── Identity Prose ──────────────────────── */}
				{identitySection && (
					<section class="narrative-section narrative-identity">
						<p>{identitySection.content}</p>
					</section>
				)}

				{/* ── Career Arc ──────────────────────────── */}
				{arcSection && (
					<section class="narrative-section">
						<h2 class="section-label">{arcSection.heading}</h2>
						<p>{arcSection.content}</p>
					</section>
				)}

				{/* ── Role Narratives ─────────────────────── */}
				{roleSections.length > 0 && (
					<section class="narrative-section">
						<h2 class="section-label">Experience</h2>
						<div class="role-list">
							{roleSections.map((section, i) => (
								<div
									key={i}
									id={section.company ? companyToSectionId(section.company) : undefined}
									class={`role-entry${focusedCompany && section.company === focusedCompany ? ' role-entry--focused' : ''}`}
								>
									<h3 class="role-heading">{section.heading}</h3>
									<p>{section.content}</p>
								</div>
							))}
						</div>
					</section>
				)}

				{/* ── Expertise ───────────────────────────── */}
				{expertiseSection && (
					<section class="narrative-section">
						<h2 class="section-label">{expertiseSection.heading}</h2>
						<p>{expertiseSection.content}</p>
					</section>
				)}

				{/* ── Perspective ─────────────────────────── */}
				{perspectiveSection && (
					<section class="narrative-section narrative-perspective">
						<h2 class="section-label">{perspectiveSection.heading}</h2>
						<p>{perspectiveSection.content}</p>
					</section>
				)}

				{/* ── BETA Entities ───────────────────────── */}
				{isPublic && betaEntities && betaEntities.length > 0 && (
					<section class="narrative-section beta-entities-section">
						<div class="beta-entities-header">
							<svg class="beta-logo" width="20" height="24" viewBox="0 0 820 976" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="86.1176" cy="889.882" r="86.1176" fill="currentColor" opacity="0.5"/>
								<path d="M384.195 633.214C359.705 575.494 336.64 512.2 319.136 447.253C323.903 424.565 327.818 402.434 331.001 381.502C337.758 337.069 341.135 298.674 342.817 271.514C343.656 257.955 344.07 247.239 344.274 240.033C344.376 236.437 344.419 233.719 344.443 231.96C344.454 231.083 344.468 230.442 344.471 230.054V229.647C344.47 160.84 322.511 132.289 305.165 121.22C297.424 116.282 290.602 114.824 287.059 114.824C276.747 114.823 238.648 127.172 230.992 201.783C239.64 107.482 287.059 0 287.059 0C307.803 0.000184641 327.159 4.01017 344.848 11.4727C415.282 41.1864 459.293 125.637 459.294 229.647V230.81C459.29 231.458 459.282 232.366 459.266 233.516C459.235 235.815 459.174 239.1 459.056 243.285C458.819 251.658 458.341 263.659 457.416 278.607C455.567 308.47 451.885 350.327 444.521 398.757C434.097 467.306 416.017 550.998 384.195 633.214Z" fill="currentColor" opacity="0.7"/>
								<path d="M341.429 678.933C284.59 550.136 229.647 384.642 229.647 229.647C229.648 130.132 287.059 2.96204e-09 287.059 2.96204e-09C183.717 -0.000626911 114.824 99.5135 114.823 229.647C114.823 408.312 177.186 591.148 236.389 725.3C265.634 791.568 294.701 847.281 316.508 886.504C327.423 906.137 336.557 921.695 343.013 932.437C346.238 937.803 348.793 941.976 350.582 944.855C351.34 946.076 351.968 947.071 352.443 947.823L353.259 949.116L353.441 949.397L353.497 949.481L353.525 949.537L370.457 976H401.882C650.341 976 798.594 848.657 817.669 696.062C826.93 621.974 803.68 547.221 750.123 491.336C696.359 435.235 615.881 401.882 516.706 401.882V516.706C589.765 516.706 638.464 540.766 667.229 570.782C696.202 601.014 708.834 641.086 703.743 681.821C694.613 754.858 621.001 850.56 433.784 860.336C428.978 852.118 423.251 842.188 416.866 830.705C396.384 793.864 368.97 741.34 341.429 678.933Z" fill="currentColor"/>
							</svg>
							<span class="beta-entities-label">Company Intelligence</span>
						</div>
						<div class="beta-entities-list">
							{betaEntities.map(entity => (
								<a
									key={entity.slug}
									href={`https://getbeta.io/company/${entity.slug}`}
									target="_blank"
									rel="noopener noreferrer"
									class="beta-entity-link"
								>
									{entity.company}
								</a>
							))}
						</div>
					</section>
				)}

				{/* ── Footer ─────────────────────────────── */}
				<footer class="narrative-footer">
					<div class="provenance">
						<span class="provenance-item">Synthesized from structured conversation</span>
						<span class="provenance-item">{narrative.provenance.claimCount} claims</span>
						{narrative.generatedAt > 0 && (
							<span class="provenance-item">{formatDate(narrative.generatedAt)}</span>
						)}
					</div>
					<div class="narrative-footer-links">
						{slug && isPublic && (
							<a href={`/b/${slug}/resume`} class="footer-link">Résumé</a>
						)}
						<a href="https://pxtxt.ai" target="_blank" rel="noopener noreferrer" class="footer-link">pxtxt</a>
					</div>
				</footer>
			</article>

			<style>{narrativeStyles}</style>
		</div>
	);
}

const narrativeStyles = `
	.narrative-container {
		max-width: 800px;
		margin: 0 auto;
		padding: 2rem;
	}

	.narrative-public {
		padding-top: 0;
	}

	.narrative {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
	}

	/* ── Identity Header ── */

	.narrative-header {
		padding: 3rem 2.5rem 2.5rem;
		border-bottom: 1px solid var(--color-border);
	}

	.narrative-name {
		margin: 0;
		font-family: var(--font-display);
		font-size: 2rem;
		font-weight: 300;
		color: var(--color-text);
		letter-spacing: 0.01em;
	}

	.narrative-title {
		margin: 0.25rem 0 0;
		font-family: var(--font-body);
		font-size: 1.0625rem;
		color: var(--color-text-muted);
	}

	.narrative-tagline {
		margin: 0.75rem 0 0;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		color: var(--color-text-subtle);
		line-height: 1.5;
	}

	.narrative-focus {
		display: flex;
		flex-wrap: wrap;
		gap: 0.375rem;
		margin-top: 1.25rem;
	}

	.narrative-focus .focus-tag {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-subtle);
	}

	.narrative-focus .focus-tag:not(:last-child)::after {
		content: '\u00B7';
		margin-left: 0.375rem;
		color: var(--color-border);
	}

	.narrative-links {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 1.25rem;
	}

	.narrative-link {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
		text-decoration: none;
		padding: 0.25rem 0;
		border-bottom: 1px solid transparent;
		transition: color 150ms, border-color 150ms;
	}

	.narrative-link:hover {
		color: var(--color-primary);
		border-bottom-color: var(--color-primary);
	}

	/* ── Sections ── */

	.narrative-section {
		padding: 2rem 2.5rem;
		border-bottom: 1px solid var(--color-border);
	}

	.narrative-section:last-of-type {
		border-bottom: none;
	}

	.narrative-section .section-label {
		margin: 0 0 1rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 400;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--color-text-subtle);
	}

	.narrative-section p {
		margin: 0;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		line-height: 1.7;
		color: var(--color-text);
	}

	/* ── Identity prose (no label, flows from header) ── */

	.narrative-identity {
		border-bottom: 1px solid var(--color-border);
	}

	.narrative-identity p {
		color: var(--color-text-muted);
		font-size: 0.9375rem;
		line-height: 1.6;
	}

	/* ── Perspective (subtle background) ── */

	.narrative-perspective {
		background: var(--color-bg);
	}

	/* ── Role entries ── */

	.role-list {
		display: flex;
		flex-direction: column;
		gap: 0;
	}

	.role-entry {
		position: relative;
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-border);
		border-left: 3px solid transparent;
		padding-left: 1rem;
		scroll-margin-top: 5rem;
		transition: background 300ms ease, border-left-color 300ms ease;
	}

	.role-entry:first-child {
		border-top: none;
		padding-top: 0;
	}

	.role-entry--focused {
		background: var(--color-primary-muted, rgba(59, 130, 246, 0.06));
		border-left-color: var(--color-primary);
		animation: role-focus-fade 3s ease forwards;
	}

	@keyframes role-focus-fade {
		0% { background: var(--color-primary-muted, rgba(59, 130, 246, 0.06)); border-left-color: var(--color-primary); }
		70% { background: var(--color-primary-muted, rgba(59, 130, 246, 0.06)); border-left-color: var(--color-primary); }
		100% { background: transparent; border-left-color: transparent; }
	}

	.role-heading {
		margin: 0 0 0.5rem;
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 300;
		color: var(--color-text);
	}

	.role-entry p {
		margin: 0 0 1.5rem;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		line-height: 1.7;
		color: var(--color-text);
	}

	.role-entry:last-child p {
		margin-bottom: 0;
	}

	/* ── Footer ── */

	.narrative-footer {
		padding: 1.25rem 2.5rem;
		background: var(--color-bg);
		border-top: 1px solid var(--color-border);
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 0.75rem;
	}

	.narrative-footer .provenance {
		display: flex;
		flex-wrap: wrap;
		gap: 0.375rem;
	}

	.narrative-footer .provenance-item {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-subtle);
	}

	.narrative-footer .provenance-item + .provenance-item::before {
		content: '\u00B7';
		margin-right: 0.375rem;
	}

	.narrative-footer-links {
		display: flex;
		gap: 0.75rem;
		font-family: var(--font-mono);
		font-size: 0.625rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	.narrative-footer .footer-link {
		color: var(--color-text-subtle);
		text-decoration: none;
	}

	.narrative-footer .footer-link:hover {
		color: var(--color-primary);
	}

	/* ── BETA Entities ── */

	.beta-entities-section {
		background: var(--color-bg);
	}

	.beta-entities-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 0.75rem;
	}

	.beta-logo {
		color: var(--color-text-subtle);
	}

	.beta-entities-label {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 400;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--color-text-subtle);
	}

	.beta-entities-list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.375rem 0.75rem;
	}

	.beta-entity-link {
		font-family: var(--font-body);
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		text-decoration: none;
		transition: color 150ms;
	}

	.beta-entity-link:hover {
		color: var(--color-primary);
	}

	/* ── Responsive ── */

	@media (max-width: 640px) {
		.narrative-container {
			padding: 1rem;
		}

		.narrative-header {
			padding: 2rem 1.5rem;
		}

		.narrative-section {
			padding: 1.5rem;
		}

		.narrative-footer {
			padding: 1rem 1.5rem;
			flex-direction: column;
			align-items: flex-start;
		}

		.narrative-name {
			font-size: 1.5rem;
		}

		.narrative-footer .provenance {
			flex-direction: column;
			gap: 0.25rem;
		}

		.narrative-footer .provenance-item + .provenance-item::before {
			content: none;
		}
	}
`;
